import { QueryLibraryTab } from '../QueryLibraryDrawer';
import { QueryTemplateRow } from '../types';

const defaultSortingMethod = (a: QueryTemplateRow, b: QueryTemplateRow) => {
  const aDataSourceName = a.datasourceName || '';
  const bDataSourceName = b.datasourceName || '';

  // Handle undefined datasource names - they should come last
  if (!aDataSourceName && bDataSourceName) {
    return 1;
  }
  if (aDataSourceName && !bDataSourceName) {
    return -1;
  }

  // Compare datasource names alphabetically
  const datasourceComparison = aDataSourceName.localeCompare(bDataSourceName);
  if (datasourceComparison !== 0) {
    return datasourceComparison;
  }

  // If datasource names are the same, compare titles alphabetically
  const aTitle = a.title || '';
  const bTitle = b.title || '';
  const localeComparison = aTitle.localeCompare(bTitle);
  if (localeComparison !== 0) {
    return localeComparison;
  }

  // If query titles are the same, compare createdAtTimestamp
  const aCreatedAt = a.createdAtTimestamp ?? 0;
  const bCreatedAt = b.createdAtTimestamp ?? 0;

  return aCreatedAt - bCreatedAt;
};

export const searchQueryLibrary = (
  queryLibrary: QueryTemplateRow[],
  query: string,
  dsFilters: string[],
  userNameFilters: string[],
  tagFilters: string[] = [],
  activeTab: QueryLibraryTab,
  userFavorites: { [key: string]: boolean },
  sortingMethod?: (a: QueryTemplateRow, b: QueryTemplateRow) => number
) => {
  const result = queryLibrary.filter((item) => {
    const lowerCaseQuery = query.toLowerCase();
    const lowerCaseDatasourceName = item.datasourceName?.toLowerCase() || '';

    // If the datasource filter is empty, or the item's datasource is in the filter, display the item
    const foundDsFilterMatch =
      dsFilters.length === 0 ||
      dsFilters.some((filter) => {
        const lowerCaseFilter = filter.toLowerCase();
        return lowerCaseDatasourceName.includes(lowerCaseFilter);
      });

    // If the user filter is empty, or the item's user is in the filter, display the item
    const userName = item.user?.displayName || '';
    const foundUserNameFilterMatch = userNameFilters.length === 0 || userNameFilters.includes(userName);

    // If the tag filter is empty, or the item has all the filtered tags, display the item
    const foundTagFilterMatch =
      tagFilters.length === 0 ||
      tagFilters.every((tagFilter) => {
        const tagFilterLowerCase = tagFilter.toLowerCase();
        return item.tags?.some((itemTag) => itemTag.toLowerCase().includes(tagFilterLowerCase));
      });

    // If the query matches any of the fields, display the item
    const queryMatchesDatasourceName = lowerCaseDatasourceName.includes(lowerCaseQuery);
    const queryMatchesDatasourceType = item.datasourceType?.toLowerCase().includes(lowerCaseQuery);
    const queryMatchesTitle = item.title?.toLowerCase().includes(lowerCaseQuery);
    const queryMatchesDescription = item.description?.toLowerCase().includes(lowerCaseQuery);
    const queryMatchesQueryText = item.queryText?.toLowerCase().includes(lowerCaseQuery);
    const queryMatchesTags = item.tags?.some((tag) => tag.toLowerCase().includes(lowerCaseQuery));

    const queryMatchesTabState = activeTab === QueryLibraryTab.ALL || userFavorites[item.uid ?? ''];

    const foundQueryMatch =
      queryMatchesDatasourceName ||
      queryMatchesDatasourceType ||
      queryMatchesTitle ||
      queryMatchesDescription ||
      queryMatchesQueryText ||
      queryMatchesTags;

    return (
      foundQueryMatch && foundDsFilterMatch && foundUserNameFilterMatch && foundTagFilterMatch && queryMatchesTabState
    );
  });

  const sortBy = sortingMethod || defaultSortingMethod;

  return result.sort(sortBy);
};
